import { useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { useSocket } from "../../../../../../context/SocketContext";

type PaymentStatus = "paid" | "failed" | "pending";
type PaymentProvider = "paypal" | "stripe" | "bank_transfer" | string;

export interface InvoicePaymentStatusPayload {
  invoiceUid: string;
  jobEventUids?: string[];
  provider: PaymentProvider;
  status: PaymentStatus;
  paidAt?: string;
  transactionId?: string | null;
  externalOrderUid?: string | null;
  fee?: number | null;
  netAmount?: number | null;
  grossAmount?: number | null;
}

export interface InvoiceUpdatedPayload {
  invoiceUid: string;
  reason: "total_changed" | "recipients_changed" | "generic";
  total?: number | null;
  currency?: string | null;
}

type Options = {
  invoiceUid: string | null | undefined;
  autoJoinRoom?: boolean;
  onEvent?: (payload: InvoicePaymentStatusPayload) => void;
  /** Fires when admins update the invoice (e.g. total changed and gateway orders rotated). */
  onUpdated?: (payload: InvoiceUpdatedPayload) => void;
};

function createAnonymousInvoiceSocket(): Socket {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
  const isLocalhost = apiUrl.includes("localhost");
  const socketUrl = apiUrl.replace("/flownect-api", "");
  const socketPath = isLocalhost ? "/socket.io/" : "/flownect-api/socket.io/";
  return io(socketUrl, {
    path: socketPath,
    transports: ["websocket"],
    reconnection: true,
  });
}

export function useInvoicePaymentStatus({ invoiceUid, autoJoinRoom = true, onEvent, onUpdated }: Options) {
  const [lastEvent, setLastEvent] = useState<InvoicePaymentStatusPayload | null>(null);
  const [history, setHistory] = useState<InvoicePaymentStatusPayload[]>([]);
  const handlerRef = useRef(onEvent);
  const updatedHandlerRef = useRef(onUpdated);
  const { socket, joinRooms } = useSocket();
  const joinedInvoiceUidRef = useRef<string | null>(null);

  useEffect(() => {
    handlerRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    updatedHandlerRef.current = onUpdated;
  }, [onUpdated]);

  const isReady = !!invoiceUid;

  useEffect(() => {
    if (!invoiceUid) return;

    const eventRoute = "invoice:paymentStatus";
    const updatedEventRoute = "invoice:updated";

    const onPaymentStatus = (raw: unknown) => {
      const payload: InvoicePaymentStatusPayload =
        typeof raw === "string" ? JSON.parse(raw as string) : (raw as InvoicePaymentStatusPayload);

      if (payload?.invoiceUid !== invoiceUid) return;

      setLastEvent(payload);
      setHistory((prev) => [payload, ...prev].slice(0, 25));
      handlerRef.current?.(payload);
    };

    const onInvoiceUpdated = (raw: unknown) => {
      const payload: InvoiceUpdatedPayload =
        typeof raw === "string" ? JSON.parse(raw as string) : (raw as InvoiceUpdatedPayload);
      if (payload?.invoiceUid !== invoiceUid) return;
      updatedHandlerRef.current?.(payload);
    };

    const attach = (s: Socket) => {
      s.on(eventRoute, onPaymentStatus);
      s.on(updatedEventRoute, onInvoiceUpdated);
    };

    const detach = (s: Socket) => {
      s.off(eventRoute, onPaymentStatus);
      s.off(updatedEventRoute, onInvoiceUpdated);
    };

    if (socket) {
      joinRooms([`room_invoice_${invoiceUid}`]);
      attach(socket);
      return () => detach(socket);
    }

    // Logged-out client invoice: SocketProvider has no socket — use a minimal connection for payment events only.
    const anon = createAnonymousInvoiceSocket();
    attach(anon);

    const doJoin = () => {
      anon.emit("join", [`room_invoice_${invoiceUid}`]);
      anon.emit("joinInvoiceRoom", invoiceUid);
    };
    anon.on("connect", doJoin);
    if (anon.connected) doJoin();

    return () => {
      anon.off("connect", doJoin);
      detach(anon);
      anon.disconnect();
    };
  }, [invoiceUid, socket, joinRooms]);

  useEffect(() => {
    if (!invoiceUid || !autoJoinRoom) return;
    if (!socket) return;

    if (joinedInvoiceUidRef.current === invoiceUid) return;

    const doJoin = () => {
      if (joinedInvoiceUidRef.current === invoiceUid) return;

      socket.emit("joinInvoiceRoom", invoiceUid);
      joinedInvoiceUidRef.current = invoiceUid;
    };

    if (socket.connected) doJoin();
    else socket.once("connect", doJoin);

    return () => {
      socket.off("connect", doJoin);

      if (joinedInvoiceUidRef.current === invoiceUid) {
        socket.emit("leaveInvoiceRoom", invoiceUid);
        joinedInvoiceUidRef.current = null;
      }
    };
  }, [invoiceUid, autoJoinRoom, socket]);

  const derived = useMemo(() => {
    const status = lastEvent?.status ?? null;
    const provider = lastEvent?.provider ?? null;
    return { status, provider };
  }, [lastEvent]);

  return {
    isReady,
    lastEvent,
    history,
    ...derived,
    clear: () => {
      setLastEvent(null);
      setHistory([]);
    },
  };
}
